import React, { FC, useEffect } from 'react';
import { ContentCollectionPlatform } from '@modernhealth/graphql';
import { CalloutInline, pxToRem } from '@modernhealth/vital-components';
import { captureException } from 'src/shared/helpers/captureException';
import { useSelector } from 'react-redux';
import { Conversation } from '@modernhealth/messaging-kit';
import styled from '@emotion/styled';

import { getCoachAssignmentUuid } from 'src/modules/user/selectors/userSelectors';
import { useProviderMessagingActionsQuery } from '../documents/getProviderMessagingActions.generated';
import { ProviderMessagingActions } from '../helpers/conversationHeaderHelpers';
import { InternalLink } from 'src/shared/components/InternalLink';
import { ExternalLink } from 'src/shared/components/ExternalLink';

const Container = styled.div`
    padding-top: ${pxToRem(15)};
`;

const ChildLink = styled.p`
    :first-of-type {
        padding-top: ${pxToRem(10)};
    }
    a {
        color: inherit;
    }
`;

export const ConversationMessageCallout: FC<{ coach: NonNullable<Conversation['coach']> }> = ({
    coach,
}) => {
    const assignmentUuid = useSelector(getCoachAssignmentUuid(coach.uuid));

    const { loading, error, data } = useProviderMessagingActionsQuery({
        variables: {
            platform: ContentCollectionPlatform.Web,
            assignmentUuid,
        },
    });

    const variant = {
        info: 'info',
        success: 'success',
        warning: 'warning',
        danger: 'danger',
        default: 'default',
    };

    useEffect(() => {
        if (error) {
            captureException(error);
        }
    }, [error]);

    const banner =
        (data?.providerMessagingActions ?? []).find(
            d => d?.name === ProviderMessagingActions.BANNER,
        )?.contentCollectionLinks[0] ?? undefined;

    if (loading || !banner) {
        return null;
    }

    const title = banner?.subtitle && banner?.title;

    return (
        <Container>
            <CalloutInline
                variant={variant[banner.labelVariant || 'default']}
                heading={title || undefined}
            >
                {banner?.subtitle || banner?.title}
                {banner.childLinks && banner.childLinks.length > 0 && (
                    <>
                        {banner.childLinks.map(link => {
                            if (!link?.path) {
                                return null;
                            }

                            return link.__typename === 'InternalLinkNode' ? (
                                <ChildLink>
                                    <InternalLink to={link.path}>{link.title}</InternalLink>
                                </ChildLink>
                            ) : (
                                <ChildLink>
                                    <ExternalLink href={link.path}>{link.title}</ExternalLink>
                                </ChildLink>
                            );
                        })}
                    </>
                )}
            </CalloutInline>
        </Container>
    );
};
